import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { ToggleRow } from '../components/conta/rows.js';
import { PageShell } from '../components/stage/PageShell.js';
import {
  GlyphBell,
  GlyphChat,
  GlyphCheckCircle,
  GlyphMoon,
  GlyphShieldCheck,
  GlyphSpark,
} from '../components/ui/Glyphs.js';
import {
  alarmsAvailable,
  alarmStatus,
  openAlarmSettings,
  testAlarm,
  type AlarmSettingsScreen,
  type AlarmStatus,
} from '../lib/alarms.js';
import { requestNotifyPermission } from '../lib/notify.js';
import { useProfile, useUpdateProfile } from '../lib/useProfile.js';
import { Skeleton } from '../components/ui/Skeleton.js';

const TEST_SECONDS = 10;

type Check = {
  id: AlarmSettingsScreen;
  ok: boolean;
  title: string;
  hint: string;
  action: string;
  /** Recomendado: ajuda, mas o lembrete toca sem isso. */
  soft?: boolean;
};

function checksOf(status: AlarmStatus): Check[] {
  return [
    {
      id: 'notifications',
      ok: status.notifications,
      title: 'Notificações do app',
      hint: status.notifications
        ? 'Liberados. É por eles que o lembrete aparece.'
        : 'Sem isso nenhum lembrete aparece na tela.',
      action: 'Liberar',
    },
    {
      id: 'exactAlarm',
      ok: status.exactAlarm,
      title: 'Tocar no minuto exato',
      hint: status.exactAlarm
        ? 'Liberado. O lembrete sai na hora, mesmo com a tela apagada.'
        : 'Sem esta permissão o aparelho pode atrasar o lembrete em alguns minutos.',
      action: 'Liberar',
    },
    {
      id: 'battery',
      ok: !status.batteryOptimized,
      title: 'Economia de bateria',
      hint: status.batteryOptimized
        ? 'O aparelho pode segurar o app em segundo plano. Deixar "sem restrição" evita atraso.'
        : 'Sem restrição para o app. Nada segura o lembrete.',
      action: 'Ajustar',
      soft: true,
    },
  ];
}

/**
 * Lembretes garantidos: mostra o que o aparelho precisa liberar para o lembrete
 * tocar na hora, deixa testar em 10 segundos e escolher o modo despertador.
 */
export function LembretesPage() {
  const [, navigate] = useLocation();
  const profileQ = useProfile();
  const update = useUpdateProfile();
  const native = alarmsAvailable();
  const [status, setStatus] = useState<AlarmStatus | null>(null);
  const [loaded, setLoaded] = useState(!native);
  const [testing, setTesting] = useState<'idle' | 'sent' | 'failed'>('idle');
  const stored = (profileQ.data?.preferences.notifications ?? {}) as Record<string, unknown>;
  const alarm = stored.alarm === true;
  const speak = stored.speak === true;

  const refresh = useCallback(() => {
    if (!native) return;
    void alarmStatus().then((next) => {
      setStatus(next);
      setLoaded(true);
    });
  }, [native]);

  // A pessoa vai à tela do sistema e volta: confere de novo quando o app reaparece.
  useEffect(() => {
    refresh();
    const onVisible = () => document.visibilityState === 'visible' && refresh();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  async function fix(check: Check) {
    if (check.id === 'notifications') {
      // Primeiro o pedido do próprio sistema; se já foi negado, a tela de configurações.
      if (await requestNotifyPermission()) return refresh();
    }
    await openAlarmSettings(check.id);
  }

  async function runTest() {
    const sent = await testAlarm(TEST_SECONDS, {
      ...(alarm ? { mode: 'alarm' as const } : {}),
      ...(speak ? { speak: true } : {}),
    });
    setTesting(sent ? 'sent' : 'failed');
  }

  const checks = status ? checksOf(status) : [];
  const missing = checks.filter((check) => !check.ok && !check.soft).length;
  const headline = !native
    ? 'No celular eu toco na hora'
    : !loaded
      ? 'Conferindo o seu aparelho'
      : missing === 0
        ? 'Tudo certo para tocar na hora'
        : missing === 1
          ? 'Falta liberar uma coisa'
          : `Falta liberar ${missing} coisas`;

  return (
    <PageShell
      kicker="Lembretes garantidos"
      title={headline}
      lead={
        native
          ? 'O lembrete toca pelo relógio do próprio aparelho, até com a tela apagada.'
          : 'No app para Android o lembrete toca com a tela apagada. Aqui no navegador, ele aparece com o app aberto.'
      }
      onBack={() => navigate('/ajustes')}
      backLabel="Voltar para Ajustes"
    >
      {native && !loaded && <Skeleton variant="rows" label="Conferindo o seu aparelho" />}

      {checks.length > 0 && (
        <section className="pgs-section" aria-label="O que o aparelho precisa">
          {checks.map((check) => (
            <div className="pgs-row ct-row" key={check.id}>
              <span className="pgs-ic">
                {check.id === 'notifications' ? (
                  <GlyphBell />
                ) : check.id === 'exactAlarm' ? (
                  <GlyphCheckCircle />
                ) : (
                  <GlyphMoon />
                )}
              </span>
              <span className="pgs-rowt">
                <b>{check.title}</b>
                <small>{check.hint}</small>
              </span>
              {check.ok ? (
                <span className="ct-seal">Pronto</span>
              ) : (
                <button type="button" className="pgs-btn small" onClick={() => void fix(check)}>
                  {check.action}
                </button>
              )}
            </div>
          ))}
        </section>
      )}

      {native && loaded && (
        <>
          <section className="pgs-section">
            <h2 className="pgs-h2">Conferir na prática</h2>
            <div className="pgs-row ct-row">
              <span className="pgs-ic">
                <GlyphSpark />
              </span>
              <span className="pgs-rowt">
                <b>Lembrete de teste</b>
                <small role="status">
                  {testing === 'sent'
                    ? `Mandei. Apague a tela e espere ${TEST_SECONDS} segundos.`
                    : testing === 'failed'
                      ? 'Não consegui agendar o teste. Confira as liberações acima.'
                      : `Toca em ${TEST_SECONDS} segundos, do mesmo jeito que um lembrete de verdade.`}
                </small>
              </span>
              <button type="button" className="pgs-btn small" onClick={() => void runTest()}>
                {testing === 'sent' ? 'De novo' : 'Testar'}
              </button>
            </div>
          </section>

          <section className="pgs-section">
            <h2 className="pgs-h2">Como eu aviso</h2>
            <ToggleRow
              on={alarm}
              onToggle={() =>
                update.mutate({ preferences: { notifications: { ...stored, alarm: !alarm } } })
              }
              icon={<GlyphBell />}
              title="Tocar como despertador"
              hint="O lembrete toca no volume do alarme, mesmo com as mídias no mudo. Compromissos seguem como aviso comum."
              disabled={update.isPending}
            />
            <ToggleRow
              on={speak}
              onToggle={() =>
                update.mutate({ preferences: { notifications: { ...stored, speak: !speak } } })
              }
              icon={<GlyphChat />}
              title="Falar o lembrete"
              hint="Depois do aviso, o aparelho lê o lembrete em voz alta. Quem estiver por perto escuta. No silencioso, só fala se o modo despertador estiver ligado."
              disabled={update.isPending}
            />
          </section>
        </>
      )}

      <section className="pgs-section">
        <div className="pgs-row ct-row">
          <span className="pgs-ic">
            <GlyphShieldCheck />
          </span>
          <span className="pgs-rowt">
            <b>Uma segunda garantia</b>
            <small>
              Se um lembrete for criado em outro aparelho e este ainda não souber dele, eu mando um
              aviso pela internet na hora marcada.
            </small>
          </span>
        </div>
        <p className="pgs-note">
          O que você apaga vai para a lixeira da Agenda e pode voltar em até 30 dias.
        </p>
      </section>
    </PageShell>
  );
}
